import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { priceFromPagesYear } from '../utils/pricing';
import { isReservationActive } from '../utils/reservations';

const makeTokens = (str = '') => {
  const words = (str || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\p{Letter}\p{Number}\s'-]+/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const tokens = new Set();
  for (const w of words) {
    const max = Math.min(w.length, 20);
    for (let i = 1; i <= max; i += 1) tokens.add(w.slice(0, i));
  }
  return Array.from(tokens);
};

const SCAN_REGION_ID = 'fullscreen-scanner-region';

function FullscreenScannerModal({ open, title, mode, onClose, error }) {
  if (!open) return null;
  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalCard}>
        <div style={styles.modalHeader}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button type="button" onClick={onClose} style={styles.ghostButton}>
            Закрити
          </button>
        </div>
        {error && <div style={styles.neutralWarn}>{error}</div>}
        <div style={styles.modalHint}>
          {mode === 'isbn' ? 'Наведіть камеру на штрихкод ISBN.' : 'Наведіть камеру на QR код користувача.'}
        </div>
        <div id={SCAN_REGION_ID} style={{ width: '100%' }} />
      </div>
    </div>
  );
}

export default function BaristaPanel() {
  const [fbUser, loadingAuth] = useAuthState(auth);
  const [authorized, setAuthorized] = useState(null);
  const [activeTab, setActiveTab] = useState('accept');
  const [scanner, setScanner] = useState({ open: false, type: null });
  const [scannerError, setScannerError] = useState('');
  const scannerRef = useRef(null);

  const [acceptIsbn, setAcceptIsbn] = useState('');
  const [acceptLookupStatus, setAcceptLookupStatus] = useState('idle');
  const [acceptLookupMessage, setAcceptLookupMessage] = useState('');
  const [bookData, setBookData] = useState({
    title: '',
    author: '',
    publisher: '',
    year: '',
    pages: '',
    coverUrl: '',
  });
  const [templateId, setTemplateId] = useState('');
  const [acceptUserUid, setAcceptUserUid] = useState('');
  const [acceptUserCard, setAcceptUserCard] = useState(null);
  const [acceptUserLoading, setAcceptUserLoading] = useState(false);
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState('');
  const [acceptError, setAcceptError] = useState('');

  const [issueIsbn, setIssueIsbn] = useState('');
  const [issueUserUid, setIssueUserUid] = useState('');
  const [issueError, setIssueError] = useState('');
  const [issueSuccess, setIssueSuccess] = useState('');
  const [issuing, setIssuing] = useState(false);

  const email = fbUser?.email || '';
  const coinsToAward = useMemo(() => {
    const pages = Number(bookData.pages) || 220;
    const year = Number(bookData.year) || new Date().getFullYear();
    return priceFromPagesYear(pages, year).base;
  }, [bookData.pages, bookData.year]);

  const lookupResolved = acceptLookupStatus === 'found' || acceptLookupStatus === 'not_found';
  const manualRequired = acceptLookupStatus === 'not_found';
  const manualFieldsReady = manualRequired
    ? Boolean(
      bookData.title.trim()
      && bookData.author.trim()
      && bookData.publisher.trim()
      && String(bookData.year).trim()
      && String(bookData.pages).trim(),
    )
    : Boolean(bookData.title.trim() && bookData.author.trim());
  const step1Complete = lookupResolved && manualFieldsReady;
  const step2Complete = Boolean(acceptUserCard?.uid);
  const summaryReady = Boolean(step1Complete && step2Complete);

  useEffect(() => {
    if (loadingAuth || authorized !== null || !fbUser?.email) return;
    (async () => {
      try {
        const cafesRef = collection(db, 'cafes');
        const emailRaw = fbUser.email.trim();
        const emailLower = emailRaw.toLowerCase();
        const markAsBarista = async () => {
          await setDoc(doc(db, 'users', fbUser.uid), { role: 'barista' }, { merge: true });
          localStorage.setItem('userRole', 'barista');
        };

        let q = query(cafesRef, where('email', '==', emailRaw));
        let snap = await getDocs(q);
        if (!snap.empty) {
          await markAsBarista();
          setAuthorized(true);
          return;
        }

        if (emailRaw !== emailLower) {
          q = query(cafesRef, where('email', '==', emailLower));
          snap = await getDocs(q);
          if (!snap.empty) {
            await markAsBarista();
            setAuthorized(true);
            return;
          }
        }

        try {
          const q2 = query(cafesRef, where('baristasLower', 'array-contains', emailLower));
          const snap2 = await getDocs(q2);
          if (!snap2.empty) {
            await markAsBarista();
            setAuthorized(true);
            return;
          }
        } catch (_) {}

        try {
          const q3 = query(cafesRef, where('uid', '==', fbUser.uid));
          const snap3 = await getDocs(q3);
          if (!snap3.empty) {
            await markAsBarista();
            setAuthorized(true);
            return;
          }
        } catch (_) {}

        setAuthorized(false);
      } catch (err) {
        console.error('Помилка перевірки доступу баристи:', err);
        setAuthorized(false);
      }
    })();
  }, [authorized, fbUser, loadingAuth]);

  useEffect(() => {
    const isbn = acceptIsbn.trim();
    if (isbn.length < 8) {
      setAcceptLookupStatus('idle');
      setAcceptLookupMessage('');
      return undefined;
    }

    const timer = setTimeout(() => {
      void findBookByIsbn(isbn);
    }, 450);

    return () => clearTimeout(timer);
  }, [acceptIsbn]);

  useEffect(() => {
    const uid = acceptUserUid.trim();
    if (!uid) {
      setAcceptUserCard(null);
      return;
    }

    let active = true;
    (async () => {
      setAcceptUserLoading(true);
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!active) return;
        if (!snap.exists()) {
          setAcceptUserCard(null);
          return;
        }
        const data = snap.data() || {};
        const name = data.name || data.displayName || data.firstName || data.username || '';
        setAcceptUserCard({
          uid,
          name,
          email: data.email || '',
          avatarUrl: data.photoURL || data.avatarUrl || '',
        });
      } catch (err) {
        console.error('Помилка завантаження користувача:', err);
        if (active) setAcceptUserCard(null);
      } finally {
        if (active) setAcceptUserLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [acceptUserUid]);

  useEffect(() => {
    let mounted = true;
    let started = false;
    if (!scanner.open || !scanner.type) return undefined;

    const isIsbnScan = scanner.type === 'accept_isbn' || scanner.type === 'issue_isbn';
    const setDecodedValue = (value) => {
      if (scanner.type === 'accept_isbn') setAcceptIsbn(value);
      if (scanner.type === 'accept_uid') setAcceptUserUid(value);
      if (scanner.type === 'issue_isbn') setIssueIsbn(value);
      if (scanner.type === 'issue_uid') setIssueUserUid(value);
    };

    const start = async () => {
      setScannerError('');
      if (scannerRef.current) return;

      let devices = [];
      try {
        devices = await Html5Qrcode.getCameras();
      } catch (err) {
        console.error('Помилка getCameras():', err);
      }

      if (!devices.length) {
        setScannerError('Камеру не знайдено. Дозвольте доступ у браузері.');
        return;
      }

      const preferred = devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[0];
      const inst = new Html5Qrcode(SCAN_REGION_ID);
      scannerRef.current = inst;

      const onDecode = (text) => {
        if (!mounted) return;
        const value = String(text || '').trim();
        if (!value) return;
        setDecodedValue(value);
        setScanner({ open: false, type: null });
      };

      const boxWidth = Math.max(220, Math.min(360, (window.innerWidth || 360) - 48));
      const config = { fps: 10, qrbox: isIsbnScan ? { width: boxWidth, height: 130 } : 250 };
      const candidates = [
        { facingMode: { exact: 'environment' } },
        { facingMode: 'environment' },
        preferred.id ? { deviceId: { exact: preferred.id } } : null,
        devices[0]?.id || null,
      ].filter(Boolean);

      let ok = false;
      for (const c of candidates) {
        try {
          await inst.start(c, config, onDecode);
          ok = true;
          started = true;
          break;
        } catch (_) {}
      }
      if (!ok) setScannerError('Не вдалося відкрити камеру. Спробуйте інший браузер або дозвіл доступу.');
    };

    void start();

    return () => {
      mounted = false;
      const inst = scannerRef.current;
      scannerRef.current = null;
      if (!inst) return;
      if (started) {
        inst.stop().then(() => inst.clear()).catch(() => {
          try {
            inst.clear();
          } catch (_) {}
        });
      } else {
        try {
          inst.clear();
        } catch (_) {}
      }
    };
  }, [scanner]);

  const findCafeByCurrentBarista = async () => {
    const cafesRef = collection(db, 'cafes');
    const emailRaw = (email || '').trim();
    const emailLower = emailRaw.toLowerCase();
    const candidates = [];
    if (emailRaw) candidates.push(query(cafesRef, where('email', '==', emailRaw)));
    if (emailLower && emailLower !== emailRaw) candidates.push(query(cafesRef, where('email', '==', emailLower)));
    if (emailLower) candidates.push(query(cafesRef, where('baristasLower', 'array-contains', emailLower)));
    if (fbUser?.uid) candidates.push(query(cafesRef, where('uid', '==', fbUser.uid)));

    for (const q of candidates) {
      try {
        const snap = await getDocs(q);
        if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
      } catch (_) {}
    }
    return null;
  };

  const findBookByIsbn = async (isbnRaw) => {
    const isbn = String(isbnRaw || '').trim();
    if (!isbn) return;
    setAcceptLookupStatus('loading');
    setAcceptLookupMessage('');
    setAcceptSuccess('');
    setAcceptError('');

    try {
      const templatesRef = collection(db, 'bookTemplates');
      const snap = await getDocs(query(templatesRef, where('isbn', '==', isbn)));
      if (!snap.empty) {
        const tplDoc = snap.docs[0];
        const data = tplDoc.data() || {};
        setTemplateId(tplDoc.id);
        setBookData({
          title: data.title || '',
          author: data.author || '',
          publisher: data.publisher || '',
          year: data.year ? String(data.year) : '',
          pages: data.pages ? String(data.pages) : '',
          coverUrl: data.coverUrl || '',
        });
        setAcceptLookupStatus('found');
        return;
      }
    } catch (err) {
      console.error('Помилка пошуку в bookTemplates:', err);
    }

    try {
      const r = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${encodeURIComponent(isbn)}&jscmd=data&format=json`);
      const data = await r.json();
      const book = data[`ISBN:${isbn}`];
      if (book) {
        const nPages = book.number_of_pages
          || (typeof book.pagination === 'string' ? Number((book.pagination.match(/\d+/) || [])[0]) : '');
        setTemplateId('');
        setBookData({
          title: book.title || '',
          author: (book.authors || []).map((a) => a.name).join(', '),
          publisher: (book.publishers || []).map((p) => p.name).join(', '),
          year: book.publish_date || '',
          pages: nPages ? String(nPages) : '',
          coverUrl: book.cover?.medium || '',
        });
        setAcceptLookupStatus('found');
        return;
      }
    } catch (err) {
      console.error('Помилка пошуку OpenLibrary:', err);
    }

    setTemplateId('');
    setBookData((prev) => ({ ...prev, coverUrl: '' }));
    setAcceptLookupStatus('not_found');
    setAcceptLookupMessage('Не знайшли книгу за ISBN. Введіть дані книги вручну або перевірте ISBN.');
  };

  const resetAcceptFlow = ({ keepSuccess = false } = {}) => {
    setAcceptIsbn('');
    setAcceptLookupStatus('idle');
    setAcceptLookupMessage('');
    setBookData({
      title: '',
      author: '',
      publisher: '',
      year: '',
      pages: '',
      coverUrl: '',
    });
    setTemplateId('');
    setAcceptUserUid('');
    setAcceptUserCard(null);
    if (!keepSuccess) setAcceptSuccess('');
    setAcceptError('');
  };

  const submitAcceptBook = async () => {
    if (!summaryReady) {
      setAcceptError('Заповніть ISBN, дані книги та QR користувача.');
      return;
    }

    setAcceptSubmitting(true);
    setAcceptError('');
    setAcceptSuccess('');

    try {
      const cafe = await findCafeByCurrentBarista();
      const isbn = acceptIsbn.trim();
      const title = bookData.title.trim();
      const author = bookData.author.trim();
      const publisher = bookData.publisher.trim();
      const year = String(bookData.year || '').trim();
      const pages = String(bookData.pages || '').trim();
      const coverUrl = String(bookData.coverUrl || '').trim();

      const pagesForPrice = pages ? Number(pages) : 220;
      const yearForPrice = year ? Number(year) : new Date().getFullYear();
      const { base: priceBase, borrow: priceBorrow } = priceFromPagesYear(pagesForPrice, yearForPrice);
      const searchTokens = Array.from(new Set([...makeTokens(title), ...makeTokens(author)]));

      const templatesRef = collection(db, 'bookTemplates');
      const tplSnap = await getDocs(query(templatesRef, where('isbn', '==', isbn)));

      let finalTemplateId = templateId;
      if (tplSnap.empty) {
        const newTpl = await addDoc(templatesRef, {
          isbn,
          title,
          author,
          publisher,
          year,
          pages: pages ? Number(pages) : null,
          coverUrl,
          titleLower: title.toLowerCase(),
          authorLower: author.toLowerCase(),
          searchTokens,
          description: '',
          category: '',
          subcategory: '',
          price: priceBase,
          borrowPrice: priceBorrow,
          priceUpdatedAt: serverTimestamp(),
          borrowCount: 0,
          availableCount: 1,
        });
        finalTemplateId = newTpl.id;
      } else {
        const tplRef = tplSnap.docs[0].ref;
        await setDoc(
          tplRef,
          {
            isbn,
            title,
            author,
            publisher,
            year,
            pages: pages ? Number(pages) : null,
            coverUrl,
            titleLower: title.toLowerCase(),
            authorLower: author.toLowerCase(),
            searchTokens,
            price: priceBase,
            borrowPrice: priceBorrow,
            priceUpdatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        await updateDoc(tplRef, { availableCount: increment(1) });
        finalTemplateId = tplRef.id;
      }

      const createdBook = await addDoc(collection(db, 'books'), {
        cafeId: cafe?.id || null,
        cafeName: cafe?.cafeName || "coffee`S",
        cafeAddress: cafe?.cafeAddress || 'невідомо',
        isbn,
        title,
        author,
        publisher,
        year,
        pages: pages ? Number(pages) : null,
        coverUrl,
        titleLower: title.toLowerCase(),
        authorLower: author.toLowerCase(),
        searchTokens,
        location: cafe?.cafeName || "coffee`S",
        status: 'available',
        createdAt: new Date().toISOString(),
        createdAtTs: serverTimestamp(),
        ownerUid: acceptUserCard.uid,
        templateId: finalTemplateId,
        price: priceBase,
        borrowPrice: priceBorrow,
        priceUpdatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'borrows'), {
        userUid: acceptUserCard.uid,
        bookId: createdBook.id,
        templateId: finalTemplateId,
        title,
        author,
        coverUrl,
        cafeId: cafe?.id || null,
        action: 'add',
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, 'users', acceptUserCard.uid), {
        balance: increment(priceBase),
        uniqueAdded: increment(1),
        updatedAt: serverTimestamp(),
      });

      resetAcceptFlow({ keepSuccess: true });
      setAcceptSuccess('Книгу прийнято. Монети нараховано.');
    } catch (err) {
      console.error('Помилка прийому книги:', err);
      setAcceptError('Не вдалося прийняти книгу. Спробуйте ще раз.');
    } finally {
      setAcceptSubmitting(false);
    }
  };

  const submitIssueBook = async () => {
    setIssuing(true);
    setIssueError('');
    setIssueSuccess('');
    try {
      const scannedUid = issueUserUid.trim();
      const isbn = issueIsbn.trim();
      if (!isbn) {
        setIssueError('Вкажіть ISBN.');
        return;
      }
      if (!scannedUid) {
        setIssueError('Проскануйте QR користувача.');
        return;
      }

      const userRef = doc(db, 'users', scannedUid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        setIssueError('Користувача не знайдено.');
        return;
      }

      let bookDoc = null;
      const reservationSnap = await getDocs(
        query(collection(db, 'bookReservations'), where('userUid', '==', scannedUid), where('isbn', '==', isbn)),
      );
      const ownActiveReservation = reservationSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .find((r) => isReservationActive(r, Date.now()));
      if (ownActiveReservation?.bookId) {
        const reservedBookRef = doc(db, 'books', String(ownActiveReservation.bookId));
        const reservedBookSnap = await getDoc(reservedBookRef);
        if (reservedBookSnap.exists() && reservedBookSnap.data()?.status === 'available') bookDoc = reservedBookSnap;
      }

      if (!bookDoc) {
        const bookSnapshot = await getDocs(
          query(collection(db, 'books'), where('isbn', '==', isbn), where('status', '==', 'available')),
        );
        if (bookSnapshot.empty) {
          setIssueError('Немає доступних книг із цим ISBN.');
          return;
        }
        bookDoc = bookSnapshot.docs[0];
      }

      const bookInfo = bookDoc.data();
      let priceToCharge = bookInfo.borrowPrice;
      if (!priceToCharge) {
        if (bookInfo.pages && bookInfo.year) {
          priceToCharge = priceFromPagesYear(bookInfo.pages, bookInfo.year).borrow;
        } else {
          const tplSnap = await getDocs(query(collection(db, 'bookTemplates'), where('isbn', '==', isbn)));
          if (!tplSnap.empty) {
            const tpl = tplSnap.docs[0].data();
            priceToCharge = tpl.borrowPrice || priceFromPagesYear(tpl.pages, tpl.year).borrow;
          } else {
            priceToCharge = priceFromPagesYear(220, new Date().getFullYear()).borrow;
          }
        }
      }

      const balance = userSnap.data().balance || 0;
      if (balance < priceToCharge) {
        setIssueError(`Недостатньо монет. Потрібно ${priceToCharge}, на балансі ${balance}.`);
        return;
      }

      await updateDoc(userRef, { balance: increment(-priceToCharge), booksTaken: increment(1), updatedAt: serverTimestamp() });
      await updateDoc(bookDoc.ref, {
        status: 'checked_out',
        issuedTo: userRef.id,
        issuedAt: new Date().toISOString(),
        lastBorrowPrice: priceToCharge,
        timesBorrowed: increment(1),
      });

      try {
        await setDoc(
          doc(db, 'bookReservations', bookDoc.id),
          {
            status: 'fulfilled',
            fulfilledAt: serverTimestamp(),
            fulfilledBy: fbUser?.uid || null,
            issuedTo: userRef.id,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      } catch (err) {
        console.warn('Не вдалося закрити бронювання:', err);
      }

      await addDoc(collection(db, 'borrows'), {
        userUid: userRef.id,
        bookId: bookDoc.id,
        templateId: bookInfo.templateId || null,
        title: bookInfo.title || '',
        author: bookInfo.author || '',
        coverUrl: bookInfo.coverUrl || '',
        cafeId: bookInfo.cafeId || null,
        action: 'take',
        createdAt: serverTimestamp(),
      });

      const tplSnap = await getDocs(query(collection(db, 'bookTemplates'), where('isbn', '==', isbn)));
      if (!tplSnap.empty) {
        await updateDoc(tplSnap.docs[0].ref, { borrowCount: increment(1), availableCount: increment(-1) });
      }

      setIssueSuccess(`Книгу видано. Списано ${priceToCharge} монет.`);
      setIssueIsbn('');
      setIssueUserUid('');
    } catch (err) {
      console.error('Помилка видачі книги:', err);
      setIssueError('Не вдалося видати книгу.');
    } finally {
      setIssuing(false);
    }
  };

  if (loadingAuth || authorized === null) return null;

  if (authorized === false) {
    return (
      <div style={styles.page}>
        <h2 style={{ marginTop: 0 }}>Панель баристи</h2>
        <div style={styles.warnBox}>
          <strong>Доступ заборонено.</strong>
          <div>Цей акаунт не прив&apos;язано до жодної кав&apos;ярні в колекції `cafes`.</div>
        </div>
      </div>
    );
  }

  const scannerTitle = (scanner.type === 'accept_isbn' || scanner.type === 'issue_isbn')
    ? 'Сканування ISBN'
    : 'Сканування QR користувача';
  const scannerMode = (scanner.type === 'accept_isbn' || scanner.type === 'issue_isbn') ? 'isbn' : 'uid';

  const renderAcceptBook = () => (
    <div style={styles.sectionCard}>
      <div style={styles.step}>
        <h3 style={styles.stepTitle}>Крок 1 - Скан ISBN (або ввести)</h3>
        <div style={styles.row}>
          <input type="text" value={acceptIsbn} onChange={(e) => setAcceptIsbn(e.target.value.trim())} placeholder="Введіть ISBN" style={styles.input} />
          <button type="button" style={styles.secondaryButton} onClick={() => setScanner({ open: true, type: 'accept_isbn' })}>
            Скан ISBN
          </button>
        </div>
        {acceptLookupStatus === 'loading' && <div style={styles.metaText}>Пошук книги...</div>}
        {acceptLookupStatus === 'found' && (
          <div style={styles.bookCard}>
            <div style={styles.coverWrap}>
              {bookData.coverUrl ? <img src={bookData.coverUrl} alt={bookData.title} style={styles.cover} /> : <div style={styles.coverFallback}>Немає обкладинки</div>}
            </div>
            <div>
              <div style={styles.bookTitle}>{bookData.title || 'Без назви'}</div>
              <div style={styles.metaText}>{bookData.author || 'Автор невідомий'}</div>
              <div style={styles.metaText}>{bookData.year || 'Рік невідомий'}</div>
            </div>
          </div>
        )}
        {acceptLookupStatus === 'found' && (
          <div style={styles.okText}>✅ Дані знайдено</div>
        )}
        {acceptLookupStatus === 'not_found' && (
          <div style={styles.neutralWarn}>
            <div>{acceptLookupMessage}</div>
          </div>
        )}
        {manualRequired && (
          <>
            <input type="text" value={bookData.title} onChange={(e) => setBookData((p) => ({ ...p, title: e.target.value }))} placeholder="Назва" style={styles.input} />
            <input type="text" value={bookData.author} onChange={(e) => setBookData((p) => ({ ...p, author: e.target.value }))} placeholder="Автор" style={styles.input} />
            <input type="text" value={bookData.publisher} onChange={(e) => setBookData((p) => ({ ...p, publisher: e.target.value }))} placeholder="Видавництво" style={styles.input} />
            <input type="text" value={bookData.year} onChange={(e) => setBookData((p) => ({ ...p, year: e.target.value }))} placeholder="Рік" style={styles.input} />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={bookData.pages}
              onChange={(e) => setBookData((p) => ({ ...p, pages: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
              placeholder="Сторінки (тільки цифри)"
              style={styles.input}
            />
          </>
        )}
      </div>

      <div style={styles.step}>
        <h3 style={styles.stepTitle}>Крок 2 - Скан QR користувача</h3>
        {!step1Complete && <div style={styles.compactHint}>Спочатку завершіть крок 1</div>}
        {step1Complete && (
          <>
            <div style={styles.row}>
              <input type="text" value={acceptUserUid} onChange={(e) => setAcceptUserUid(e.target.value.trim())} placeholder="UID користувача" style={styles.input} />
              <button type="button" style={styles.secondaryButton} onClick={() => setScanner({ open: true, type: 'accept_uid' })}>
                Скан QR
              </button>
            </div>
            {acceptUserLoading && <div style={styles.metaText}>Завантаження користувача...</div>}
            {acceptUserCard && (
              <div style={styles.userCard}>
                {acceptUserCard.avatarUrl ? (
                  <img src={acceptUserCard.avatarUrl} alt={acceptUserCard.name || 'Avatar'} style={styles.avatar} />
                ) : (
                  <div style={styles.avatarFallback}>{(acceptUserCard.name || 'U').charAt(0).toUpperCase()}</div>
                )}
                <div>
                  <div style={styles.bookTitle}>{acceptUserCard.name || 'Користувач без імені'}</div>
                  <div style={styles.metaText}>{acceptUserCard.email || 'email не вказано'}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div style={styles.step}>
        <h3 style={styles.stepTitle}>Крок 3 - Підтвердження операції</h3>
        {!step2Complete && <div style={styles.compactHint}>Спочатку завершіть крок 2</div>}
        {step2Complete && (
          <>
            <div style={styles.summary}>
              <div>Ви приймаєте книгу:</div>
              <div><strong>{bookData.title || 'Назва відсутня'}</strong> / {bookData.author || 'Автор відсутній'}</div>
              <div>Локація: {`coffee\`S`}</div>
              <div>Користувач: {acceptUserCard?.name || 'не обрано'} ({acceptUserCard?.email || 'email...'})</div>
              <div>Нарахування: +{coinsToAward} монет</div>
            </div>
            <button type="button" style={styles.primaryBig} disabled={!summaryReady || acceptSubmitting} onClick={submitAcceptBook}>
              {acceptSubmitting ? 'Збереження...' : 'Підтвердити'}
            </button>
          </>
        )}
      </div>

      {acceptError && <div style={styles.errorText}>{acceptError}</div>}
      {acceptSuccess && (
        <div style={styles.successBox}>
          <div>{acceptSuccess}</div>
        </div>
      )}
    </div>
  );

  const renderIssueBook = () => (
    <div style={styles.sectionCard}>
      <h3 style={styles.stepTitle}>Видати книгу</h3>
      <div style={styles.row}>
        <input type="text" placeholder="Введіть ISBN книги" value={issueIsbn} onChange={(e) => setIssueIsbn(e.target.value.trim())} style={styles.input} />
        <button type="button" style={styles.secondaryButton} onClick={() => setScanner({ open: true, type: 'issue_isbn' })}>
          Скан ISBN
        </button>
      </div>
      <div style={styles.row}>
        <input type="text" placeholder="Скануйте QR користувача" value={issueUserUid} onChange={(e) => setIssueUserUid(e.target.value.trim())} style={styles.input} />
        <button type="button" style={styles.secondaryButton} onClick={() => setScanner({ open: true, type: 'issue_uid' })}>
          Скан QR
        </button>
      </div>
      <button type="button" style={styles.primaryBig} onClick={submitIssueBook} disabled={issuing}>
        {issuing ? 'Обробка...' : 'Видати книгу'}
      </button>
      {issueError && <div style={styles.errorText}>{issueError}</div>}
      {issueSuccess && <div style={styles.successBox}>{issueSuccess}</div>}
    </div>
  );

  return (
    <div style={styles.page}>
      <h2 style={{ marginTop: 0 }}>Панель баристи</h2>
      <div style={styles.tabWrap}>
        <button type="button" onClick={() => setActiveTab('accept')} style={activeTab === 'accept' ? styles.tabActive : styles.tab}>
          Прийняти книгу
        </button>
        <button type="button" onClick={() => setActiveTab('issue')} style={activeTab === 'issue' ? styles.tabActive : styles.tab}>
          Видати книгу
        </button>
      </div>

      {activeTab === 'accept' ? renderAcceptBook() : renderIssueBook()}

      <FullscreenScannerModal
        open={scanner.open}
        title={scannerTitle}
        mode={scannerMode}
        error={scannerError}
        onClose={() => setScanner({ open: false, type: null })}
      />
    </div>
  );
}

const styles = {
  page: { maxWidth: 760, margin: '0 auto', padding: 20 },
  tabWrap: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginBottom: 16,
    background: '#f3f4f6',
    borderRadius: 12,
    padding: 6,
  },
  tab: {
    border: 'none',
    borderRadius: 10,
    padding: '12px 10px',
    background: 'transparent',
    fontWeight: 700,
    cursor: 'pointer',
  },
  tabActive: {
    border: '1px solid #0f766e',
    borderRadius: 10,
    padding: '12px 10px',
    background: '#ffffff',
    color: '#0f766e',
    fontWeight: 800,
    cursor: 'pointer',
  },
  sectionCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 16,
    background: '#fff',
    display: 'grid',
    gap: 14,
  },
  step: { border: '1px solid #eef2f7', borderRadius: 12, padding: 12, display: 'grid', gap: 10 },
  compactHint: { color: '#64748b', fontSize: 13 },
  okText: { color: '#15803d', fontSize: 14, fontWeight: 700 },
  stepTitle: { margin: 0, fontSize: 18 },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  input: {
    flex: 1,
    minWidth: 240,
    height: 42,
    border: '1px solid #d1d5db',
    borderRadius: 10,
    padding: '0 12px',
    fontSize: 15,
  },
  secondaryButton: {
    border: '1px solid #cbd5e1',
    borderRadius: 10,
    height: 42,
    background: '#f8fafc',
    padding: '0 14px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  inlineButton: {
    border: 'none',
    background: '#eef2ff',
    color: '#312e81',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  primaryBig: {
    width: '100%',
    minHeight: 48,
    border: 'none',
    borderRadius: 12,
    background: '#15803d',
    color: '#fff',
    fontSize: 16,
    fontWeight: 800,
    cursor: 'pointer',
    padding: '10px 16px',
  },
  neutralWarn: {
    border: '1px solid #facc15',
    background: '#fef9c3',
    color: '#713f12',
    borderRadius: 10,
    padding: 12,
    display: 'grid',
    gap: 10,
  },
  warnBox: { padding: 14, borderRadius: 10, border: '1px solid #fde68a', background: '#fef9c3' },
  successBox: {
    border: '1px solid #86efac',
    background: '#f0fdf4',
    color: '#14532d',
    borderRadius: 10,
    padding: 12,
    display: 'grid',
    gap: 10,
  },
  errorText: {
    border: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#991b1b',
    borderRadius: 10,
    padding: 10,
  },
  summary: { border: '1px dashed #cbd5e1', borderRadius: 10, padding: 12, display: 'grid', gap: 8, color: '#111827' },
  bookCard: {
    display: 'grid',
    gridTemplateColumns: '84px 1fr',
    gap: 12,
    alignItems: 'start',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 10,
  },
  coverWrap: { width: 84, height: 120, borderRadius: 8, overflow: 'hidden', background: '#f3f4f6' },
  cover: { width: '100%', height: '100%', objectFit: 'cover' },
  coverFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
    padding: 6,
  },
  bookTitle: { fontWeight: 800, color: '#111827', marginBottom: 4 },
  metaText: { color: '#6b7280', fontSize: 14 },
  userCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 10,
    display: 'grid',
    gridTemplateColumns: '48px 1fr',
    gap: 10,
    alignItems: 'center',
  },
  avatar: { width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' },
  avatarFallback: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#e2e8f0',
    color: '#334155',
    fontWeight: 800,
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(15, 23, 42, 0.78)',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    padding: 12,
  },
  modalCard: {
    background: '#fff',
    borderRadius: 14,
    width: '100%',
    maxWidth: 760,
    display: 'grid',
    alignContent: 'start',
    gap: 12,
    padding: 12,
    overflow: 'auto',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalHint: { color: '#475569', fontSize: 14 },
  ghostButton: {
    border: '1px solid #cbd5e1',
    borderRadius: 10,
    height: 36,
    background: '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
};
